import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DatePicker from 'react-datepicker';

import FormGroup from '../../../../../../components/global/FormGroup';
import PhoneInput from '../../../../../../components/global/PhoneInput';
import Alert from '../../../../../../components/global/Alert';
import Button from '../../../../../../components/global/Button';
import { getFormikTouchedErrors } from '../../../../../../util/formik';
import { isValidPhone } from '../../../../../../util/phone';
import CalendarIcon from '../../../../../../components/icons/CalendarIcon';
import InputGroup from '../../../../../../components/global/InputGroup';
import useVirtualPageView from '../../../../../pro/services/analytics/useVirtualPageView';

const propTypes = {
  className: PropTypes.string,
  values: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    phone_number: PropTypes.string,
    email_address: PropTypes.string,
  }).isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

const RegistrationForm = ({
  className,
  handleSubmit,
  values,
  errors,
  touched,
  loading,
  handleChange,
  handleBlur,
}) => {
  const classes = classNames({
    'view-register-form': true,
    [className]: !!className,
  });

  const errorList = Object.values(
    getFormikTouchedErrors(errors, touched) ?? {},
  );

  useVirtualPageView({
    title: 'Reserve Waldo for your wedding today! - Phone Authentication',
    path: '/phone-auth',
  });

  const DatePickerCustomInput = forwardRef(({ value, onClick }, ref) => (
    <InputGroup
      id="date"
      name="date"
      label="Wedding Date"
      labelNote="(optional)"
      onClick={onClick}
      onBlur={handleBlur}
      value={value}
      addon={<CalendarIcon size={14} color="#8c9699" />}
      inputRef={ref}
    />
  ));

  return (
    <form className={classes} onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-lg-6 col-12">
          <FormGroup
            id="first_name"
            name="first_name"
            label="First name"
            required
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.first_name}
          />
        </div>

        <div className="col-lg-6 col-12">
          <FormGroup
            id="last_name"
            name="last_name"
            label="Last name"
            required
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.last_name}
          />
        </div>

        <div className="col-lg-6 col-12">
          <PhoneInput
            id="phone_number"
            value={values.phone_number || '+1'}
            label="Mobile Phone"
            onChange={phone => {
              handleChange({
                target: {
                  name: 'phone_number',
                  value: phone,
                },
              });
            }}
            className="mb-2"
          />
        </div>

        <div className="col-lg-6 col-12">
          <DatePicker
            id="date"
            name="date"
            customInput={<DatePickerCustomInput />}
            selected={values.date}
            onChange={date => {
              handleChange({
                target: {
                  name: 'date',
                  value: date,
                },
              });
            }}
          />
        </div>
        <div className="col-12">
          <FormGroup
            id="email_address"
            name="email_address"
            label="Email Address"
            required
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email_address}
          />
        </div>
      </div>

      {errorList?.length > 0 && (
        <Alert type="danger" className="mb-4">
          <ul className="mb-0">
            {errorList.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <div className="text-center">
        <Button
          type="submit"
          btnSize="lg"
          disabled={loading || !isValidPhone(values.phone_number)}
          loading={loading}
          className="mt-1 view-register-cta"
        >
          {loading ? 'Processing...' : 'Request PIN Code to Verify'}
        </Button>
      </div>
    </form>
  );
};

RegistrationForm.propTypes = propTypes;

export default RegistrationForm;
